import { useState, useMemo, useEffect, useRef, Suspense, lazy } from "react";
import { ThemeProvider, CssBaseline, Box, CircularProgress, useMediaQuery } from "@mui/material";
import type { PaletteMode } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useLocation,
  useParams,
  useNavigationType,
} from "react-router-dom";
import type { Location } from "react-router-dom";
import Cookies from "js-cookie";
import { ClientHelmet } from "./components/common/ClientHelmet";
import { MotionConfig } from "framer-motion";
import { getTheme } from "./theme";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { SnackbarProvider } from "notistack";
import { useSnackbar } from "notistack";
import { CookieProvider } from "./contexts/CookieProvider";
import { ColorModeContext } from "./contexts/ColorModeContext";
import { CookieBanner } from "./components/cookies/CookieBanner";
import { CookieSettingsModal } from "./components/cookies/CookieSettingsModal";
import { ScrollToTop } from "./components/utils/ScrollToTop";
import { RouteTracker } from "./components/utils/RouteTracker";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminRoute from "./components/auth/AdminRoute";
import { OnboardingCheck } from "./components/auth/OnboardingCheck";
import { SignalPostMeta } from "./components/signal/SignalPostMeta";
import { useSignalPosts } from "./hooks/useSignalPosts";
import type { SignalPost as SignalPostType } from "./types/signal";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { HeadSsrAdoption } from "./components/common/HeadSsrAdoption";
import { UIProvider, useUI } from "./contexts/UIContext";
import { ActualLocationContext } from "./contexts/ActualLocationContext";
import { supabase } from "./lib/supabase";

// Lazy load pages for performance
const Home = lazy(() => import("./pages/Home").then((module) => ({ default: module.Home })));
const Training = lazy(() =>
  import("./pages/Training").then((module) => ({ default: module.Training }))
);
const Certifications = lazy(() =>
  import("./pages/Certifications").then((module) => ({
    default: module.Certifications,
  }))
);
const About = lazy(() => import("./pages/About").then((module) => ({ default: module.About })));
const Contact = lazy(() =>
  import("./pages/Contact").then((module) => ({ default: module.Contact }))
);
const Enterprise = lazy(() =>
  import("./pages/Enterprise").then((module) => ({
    default: module.Enterprise,
  }))
);
const Benchmarks = lazy(() =>
  import("./pages/resources/Benchmarks").then((module) => ({
    default: module.Benchmarks,
  }))
);
const Blog = lazy(() => import("./pages/Blog").then((module) => ({ default: module.Blog })));
const BlogPost = lazy(() =>
  import("./pages/BlogPost").then((module) => ({ default: module.BlogPost }))
);
const Signal = lazy(() => import("./pages/Signal").then((module) => ({ default: module.Signal })));
const SignalPost = lazy(() =>
  import("./pages/SignalPost").then((module) => ({ default: module.SignalPost }))
);
const Resources = lazy(() =>
  import("./pages/Resources").then((module) => ({ default: module.Resources }))
);
const Prompting = lazy(() =>
  import("./pages/resources/Prompting").then((module) => ({
    default: module.Prompting,
  }))
);
const AiIdes = lazy(() =>
  import("./pages/resources/AiIdes").then((module) => ({
    default: module.AiIdes,
  }))
);
const AiClis = lazy(() =>
  import("./pages/resources/AiClis").then((module) => ({
    default: module.AiClis,
  }))
);
const ModelCheatSheet = lazy(() =>
  import("./pages/resources/ModelCheatSheet").then((module) => ({
    default: module.ModelCheatSheet,
  }))
);
const AiSdlc = lazy(() =>
  import("./pages/resources/AiSdlc").then((module) => ({
    default: module.AiSdlc,
  }))
);
const AiGovernance = lazy(() =>
  import("./pages/resources/AiGovernance").then((module) => ({
    default: module.AiGovernance,
  }))
);
const Account = lazy(() =>
  import("./pages/Account").then((module) => ({ default: module.Account }))
);
const UpdatePassword = lazy(() =>
  import("./pages/UpdatePassword").then((module) => ({
    default: module.UpdatePassword,
  }))
);
const NotFound = lazy(() =>
  import("./pages/NotFound").then((module) => ({ default: module.NotFound }))
);
const AgentKits = lazy(() =>
  import("./pages/AgentKits").then((module) => ({ default: module.AgentKits }))
);
const AgentKitDetail = lazy(() =>
  import("./pages/AgentKitDetail").then((module) => ({ default: module.AgentKitDetail }))
);
const DynamicCourseDetail = lazy(() =>
  import("./pages/DynamicCourseDetail").then((module) => ({
    default: module.DynamicCourseDetail,
  }))
);
const DynamicCoursePlayer = lazy(() =>
  import("./pages/DynamicCoursePlayer").then((module) => ({
    default: module.DynamicCoursePlayer,
  }))
);
const PaymentSuccess = lazy(() =>
  import("./pages/PaymentSuccess").then((module) => ({
    default: module.PaymentSuccess,
  }))
);
const AdminCourses = lazy(() =>
  import("./pages/admin/AdminCourses").then((module) => ({
    default: module.AdminCourses,
  }))
);
const CourseEditor = lazy(() =>
  import("./pages/admin/CourseEditor").then((module) => ({
    default: module.CourseEditor,
  }))
);
const AdminSignal = lazy(() =>
  import("./pages/admin/AdminSignal").then((module) => ({
    default: module.AdminSignal,
  }))
);
const SignalEditor = lazy(() =>
  import("./pages/admin/SignalEditor").then((module) => ({
    default: module.SignalEditor,
  }))
);
const AdminBlog = lazy(() =>
  import("./pages/admin/AdminBlog").then((module) => ({
    default: module.AdminBlog,
  }))
);
const BlogEditor = lazy(() =>
  import("./pages/admin/BlogEditor").then((module) => ({
    default: module.BlogEditor,
  }))
);

const CaadCertDetail = lazy(() =>
  Promise.all([
    import("./components/training/GenericCourseDetail"),
    import("./data/certifications/caad"),
  ]).then(([mod, data]) => ({
    default: () => (
      <mod.GenericCourseDetail
        course={data.caadCertificationData}
        curriculumTitle="Certification Tasks"
      />
    ),
  }))
);
const CaadCertExam = lazy(() =>
  Promise.all([
    import("./components/training/CoursePlayer"),
    import("./data/certifications/caad"),
  ]).then(([mod, data]) => ({
    default: () => (
      <mod.default course={data.caadCertificationData} basePath="/certification/caad/exam" />
    ),
  }))
);

const LoginModal = lazy(() =>
  import("./components/modals/LoginModal").then((module) => ({
    default: module.LoginModal,
  }))
);

const GlobalUI = () => {
  const { isLoginModalOpen, closeLoginModal } = useUI();
  const { enqueueSnackbar } = useSnackbar();
  const hasShownConfirmation = useRef(false);

  // Detect email confirmation via Supabase auth events
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      // Supabase fires SIGNED_IN when a user confirms their email via the link.
      // We check the URL hash for type=signup to distinguish from normal logins.
      if (event === "SIGNED_IN" && !hasShownConfirmation.current) {
        const hash = window.location.hash;
        if (hash.includes("type=signup") || hash.includes("type=email_change")) {
          hasShownConfirmation.current = true;
          enqueueSnackbar("Email confirmed - welcome aboard!", { variant: "success" });
          // Clean the hash fragments from the URL while preserving query parameters
          window.history.replaceState(
            null,
            "",
            `${window.location.pathname}${window.location.search}`
          );
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [enqueueSnackbar]);

  return (
    <Suspense fallback={null}>
      <LoginModal open={isLoginModalOpen} onClose={closeLoginModal} />
    </Suspense>
  );
};

const MainLayout = () => (
  <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
    <ClientHelmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://obsidianridge.ai/#organization",
              name: "Obsidian Ridge AI",
              alternateName: "Obsidian Ridge",
              url: "https://obsidianridge.ai/",
              logo: {
                "@type": "ImageObject",
                "@id": "https://obsidianridge.ai/#logo",
                url: "https://obsidianridge.ai/logo.webp",
                contentUrl: "https://obsidianridge.ai/logo.webp",
                width: 200,
                height: 203,
              },
              description: "Verified AI engineering signal for teams that ship.",
              foundingDate: "2025",
              founder: [
                {
                  "@type": "Person",
                  "@id": "https://obsidianridge.ai/about#cody-karkoska",
                  name: "Cody Karkoska",
                  url: "https://obsidianridge.ai/about#cody-karkoska",
                },
                {
                  "@type": "Person",
                  "@id": "https://obsidianridge.ai/about#kaleb-levi",
                  name: "Kaleb Levi",
                  url: "https://obsidianridge.ai/about#kaleb-levi",
                },
              ],
              sameAs: [
                "https://x.com/ObsidianRidgeAI",
                "https://www.linkedin.com/in/ObsidianRidgeAI",
                "https://www.youtube.com/@ObsidianRidgeAI",
                "https://github.com/ObsidianRidgeAI",
                "https://www.tiktok.com/@obsidianridgeai",
                "https://www.facebook.com/profile.php?id=61584397516546",
                "https://www.instagram.com/obsidianridgeai/",
                "https://www.reddit.com/user/ObsidianRidgeAI/",
              ],
              knowsAbout: [
                "AI coding tools",
                "AI coding agents",
                "Developer productivity",
                "GitHub Copilot",
                "Cursor",
                "Claude Code",
                "Codex",
                "Gemini CLI",
                "Aider",
                "Prompt engineering",
                "Software development lifecycle",
                "AI governance",
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://obsidianridge.ai/#website",
              name: "Obsidian Ridge AI",
              alternateName: "Obsidian Ridge",
              url: "https://obsidianridge.ai/",
              publisher: { "@id": "https://obsidianridge.ai/#organization" },
            },
          ],
        })}
      </script>
    </ClientHelmet>
    <HeadSsrAdoption />
    <Header />
    <Box component="main" id="main-content" sx={{ flexGrow: 1 }}>
      <ErrorBoundary>
        <Suspense
          fallback={
            <Box
              sx={{
                py: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={40} aria-label="Loading page" />
            </Box>
          }
        >
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </Box>
    <Footer />
  </Box>
);

function App() {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = Cookies.get("theme_mode");
    if (savedMode === "dark" || savedMode === "light") return savedMode;
    return prefersDark ? "dark" : "light";
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          Cookies.set("theme_mode", newMode, {
            expires: 365,
            sameSite: "Lax",
            secure: window.location.protocol === "https:",
          });
          return newMode;
        });
      },
    }),
    []
  );

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <CookieProvider>
      <UIProvider>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            {/* reducedMotion="user" makes every framer-motion animation fall
                back to opacity-only (or none) for OS reduced-motion users. */}
            <MotionConfig reducedMotion="user">
              <SnackbarProvider
                maxSnack={3}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <Router>
                  <ScrollToTop />
                  <RouteTracker />
                  <OnboardingCheck />
                  <GlobalUI />
                  <AppRoutes />
                  <CookieBanner />
                  <CookieSettingsModal />
                </Router>
              </SnackbarProvider>
            </MotionConfig>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </UIProvider>
    </CookieProvider>
  );
}

export default App;

// ── AppRoutes ────────────────────────────────────────────────────────────────
//
// Lives inside <Router> so we can use `useLocation`. Implements the
// "background location" modal pattern for `/signal/:slug`:
//
//   • When the user clicks a Signal card, we navigate to /signal/:slug
//     while stashing the current location in `state.background`.
//   • Here, we render the routes against the BACKGROUND location (so the
//     Signal feed stays mounted) and ALSO render an overlay <Routes> for
//     the modal - currently just a Helmet block via SignalPostMeta to win
//     `react-helmet-async` dedupe with the post's metadata.
//   • When `state.background` is absent (direct hit / share / RSS click),
//     `/signal/:slug` renders the standalone <SignalPost /> page.

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const state = location.state as { background?: Location } | null;
  // Only honor `state.background` on PUSH/REPLACE navigations (i.e., the
  // user clicked a Signal card inside the SPA). On POP - which fires for
  // hard reloads, browser back/forward, or any URL the user pasted that
  // still has a stale `background` in its history entry - ignore it so
  // `/signal/:slug` resolves to the standalone <SignalPost /> page.
  const background = navigationType === "POP" ? undefined : state?.background;

  return (
    <ActualLocationContext.Provider value={location}>
      <Routes location={background ?? location}>
        <Route element={<MainLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/signal" element={<Signal />} />
          <Route path="/signal/:slug" element={<SignalPost />} />

          {/* Admin-only: Training, Certifications & Enterprise (hidden from public, will be re-enabled later) */}
          <Route element={<AdminRoute />}>
            <Route path="/training" element={<Training />} />
            <Route path="/certifications" element={<Certifications />} />
            <Route path="/enterprise" element={<Enterprise />} />
          </Route>
          <Route path="/resources/benchmarks" element={<Benchmarks />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/prompting-guide" element={<Prompting />} />
          <Route path="/resources/ai-ides" element={<AiIdes />} />
          <Route path="/resources/ai-clis" element={<AiClis />} />
          <Route path="/resources/model-cheat-sheet" element={<ModelCheatSheet />} />
          <Route path="/resources/ai-sdlc" element={<AiSdlc />} />
          <Route path="/resources/ai-governance" element={<AiGovernance />} />
          <Route path="/agent-kits" element={<AgentKits />} />
          <Route path="/agent-kits/:slug" element={<AgentKitDetail />} />

          {/* Admin-only: Dynamic course detail */}
          <Route element={<AdminRoute />}>
            <Route path="/training/:slug" element={<DynamicCourseDetail />} />
          </Route>

          <Route path="/certification/caad" element={<CaadCertDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/account" element={<Account />} />
          </Route>

          {/* Password reset - handles its own auth state for expired-link UX */}
          <Route path="/update-password" element={<UpdatePassword />} />

          {/* Admin-only: Course management */}
          <Route element={<AdminRoute />}>
            <Route path="/admin/courses" element={<AdminCourses />} />
            <Route path="/admin/courses/:id" element={<CourseEditor />} />
          </Route>

          {/* Admin-only: Signal Desk management */}
          <Route element={<AdminRoute />}>
            <Route path="/admin/signal-desk" element={<AdminSignal />} />
            <Route path="/admin/signal-desk/:id" element={<SignalEditor />} />
          </Route>

          {/* Admin-only: Blog management */}
          <Route element={<AdminRoute />}>
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/blog/:id" element={<BlogEditor />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Course Player Routes (No Header/Footer) - Admin only */}
        <Route element={<AdminRoute />}>
          <Route
            path="/training/:slug/learn/*"
            element={
              <ErrorBoundary>
                <Suspense fallback={<CircularProgress />}>
                  <DynamicCoursePlayer />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/certification/caad/exam/*"
            element={
              <ErrorBoundary>
                <Suspense fallback={<CircularProgress />}>
                  <CaadCertExam />
                </Suspense>
              </ErrorBoundary>
            }
          />
        </Route>
      </Routes>

      {/* Modal overlay: when /signal/:slug is the *real* URL but a
          background route is what's rendered, mount a Helmet-only route
          so the post's metadata wins react-helmet-async dedupe. The
          actual visual modal is rendered inside <Signal /> based on the
          URL slug. */}
      {background && (
        <Routes>
          <Route path="/signal/:slug" element={<SignalPostMetaRoute />} />
        </Routes>
      )}
    </ActualLocationContext.Provider>
  );
};

/**
 * Helmet-only route used in the modal-overlay <Routes>. Fetches the
 * Signal post by slug (cached server-side via the worker; client-side
 * the same `useSignalPosts` Supabase fetch the Signal page uses) and
 * renders <SignalPostMeta>. This is what makes shared/back-navigated
 * `/signal/:slug` URLs surface post-specific OG/Twitter/JSON-LD even
 * while the Home/Signal feed is the visible background.
 */
const SignalPostMetaRoute: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { getPostBySlug } = useSignalPosts();
  // Track the slug alongside the post so a rapid /signal/foo → /signal/bar
  // navigation never flashes foo's metadata while bar is still fetching.
  // We never call setState synchronously in the effect - instead we render
  // null whenever loaded.slug !== current slug.
  const [loaded, setLoaded] = useState<{ slug: string; post: SignalPostType | null } | null>(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    void getPostBySlug(slug).then((next) => {
      if (!cancelled) setLoaded({ slug, post: next });
    });
    return () => {
      cancelled = true;
    };
  }, [slug, getPostBySlug]);
  if (!slug || !loaded || loaded.slug !== slug || !loaded.post) return null;
  return <SignalPostMeta post={loaded.post} />;
};
